"use client";
import EmptyState from "../../../components/custom/empty-state";
import {
  MEASURE_TYPE,
  MEASURE_TYPE_NAME,
  UNIT_STATUS,
  UNIT_STATUS_NAME,
  Unit,
} from "../services/unit-service";

interface UnitsTableProps {
  rows: Unit[];
  isLoading: boolean;
  onEdit?: (row: Unit) => void;
  onToggleStatus?: (row: Unit) => void;
}

const statusColor: Record<number, string> = {
  [UNIT_STATUS.ACTIVE]: "bg-green-100 text-green-800",
  [UNIT_STATUS.INACTIVE]: "bg-red-100 text-red-800",
};

const measureColor: Record<number, string> = {
  [MEASURE_TYPE.COUNT]: "bg-blue-100 text-blue-800",
  [MEASURE_TYPE.WEIGHT]: "bg-teal-100 text-teal-800",
  [MEASURE_TYPE.OCCURRENCE]: "bg-amber-100 text-amber-800",
};

const formatFactor = (v: number) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 6 }).format(v || 0);

const UnitsTable: React.FC<UnitsTableProps> = ({ rows, isLoading, onEdit, onToggleStatus }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <svg className="animate-spin h-6 w-6 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm font-medium">Đang tải dữ liệu...</span>
      </div>
    );
  }

  if (!rows.length) {
    return <EmptyState message="Chưa có đơn vị nào" />;
  }

  const byId: Record<number, Unit> = {};
  for (const u of rows) byId[u.id] = u;

  return (
    <table className="min-w-full border text-sm">
      <thead className="bg-gray-100">
        <tr>
          <th className="border px-3 py-2 text-center w-16">STT</th>
          <th className="border px-3 py-2 text-center w-28">Mã</th>
          <th className="border px-3 py-2 text-left w-40">Tên đơn vị</th>
          <th className="border px-3 py-2 text-center w-52">Nhóm đo lường</th>
          <th className="border px-3 py-2 text-left">Quy đổi</th>
          <th className="border px-3 py-2 text-center w-24">Thứ tự</th>
          <th className="border px-3 py-2 text-center w-36">Trạng thái</th>
          <th className="border px-3 py-2 text-center w-28">Thao tác</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => {
          const base = row.base_unit_id ? byId[row.base_unit_id] : undefined;
          return (
            <tr key={row.id} className="hover:bg-gray-50">
              <td className="border px-3 py-2 text-center">{idx + 1}</td>
              <td className="border px-3 py-2 text-center font-mono text-gray-600">{row.code}</td>
              <td className="border px-3 py-2 font-medium">{row.name}</td>
              <td className="border px-3 py-2 text-center">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    measureColor[row.measure_type] || "bg-gray-100 text-gray-800"
                  }`}
                >
                  {MEASURE_TYPE_NAME[row.measure_type] || "—"}
                </span>
              </td>
              <td className="border px-3 py-2 text-gray-600">
                {/* Viết thành câu người đọc hiểu ngay, thay vì bày hai con số rời rạc. */}
                {row.base_unit_id && base ? (
                  <span className="tabular-nums">
                    1 {row.name} = {formatFactor(row.factor)} {base.name}
                  </span>
                ) : (
                  <span className="text-gray-400">Đơn vị gốc</span>
                )}
              </td>
              <td className="border px-3 py-2 text-center">{row.sort_order}</td>
              <td className="border px-3 py-2 text-center">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    statusColor[row.status] || "bg-gray-100 text-gray-800"
                  }`}
                >
                  {UNIT_STATUS_NAME[row.status] || "—"}
                </span>
              </td>
              <td className="border px-3 py-2">
                <div className="flex justify-center gap-2">
                  {onEdit && (
                    <button
                      type="button"
                      onClick={() => onEdit(row)}
                      aria-label="Sửa"
                      className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-yellow-500 text-white shadow-sm hover:bg-yellow-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652l-9.547 9.546a4.5 4.5 0 01-1.897 1.13l-3.034.909.91-3.034a4.5 4.5 0 011.129-1.897l9.547-9.546z" />
                      </svg>
                      <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/action:block">
                        Sửa
                      </span>
                    </button>
                  )}
                  {onToggleStatus && (
                    <button
                      type="button"
                      onClick={() => onToggleStatus(row)}
                      aria-label={row.status === UNIT_STATUS.ACTIVE ? "Ngưng sử dụng" : "Dùng lại"}
                      className={`group/action relative grid h-9 w-9 place-items-center rounded-full text-white shadow-sm ${
                        row.status === UNIT_STATUS.ACTIVE
                          ? "bg-orange-500 hover:bg-orange-600"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                    >
                      {row.status === UNIT_STATUS.ACTIVE ? (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                        </svg>
                      )}
                      <span className="pointer-events-none absolute -top-8 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover/action:block">
                        {row.status === UNIT_STATUS.ACTIVE ? "Ngưng sử dụng" : "Dùng lại"}
                      </span>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default UnitsTable;
